import { CaretRightOutlined } from "@ant-design/icons";
import { Avatar, Button, Collapse, Spin, Tooltip } from "antd";
import classNames from "clsx";
import React, { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import rehypeKatex from "rehype-katex";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";

import UserAvatar from "@/components/UserAvatar";
import { useUserStore } from "@/store/zustand/useUserStore";

import mantraIcon from "./images/mantra-icon.png";

const remarkPlugins = [remarkGfm, remarkMath];
const rehypePlugins = [rehypeKatex, rehypeRaw];
const { Panel } = Collapse;

const ChainlitMarkdownMessagePart = ({ content }) => (
  <ReactMarkdown
    remarkPlugins={remarkPlugins}
    rehypePlugins={rehypePlugins}
    components={{
      a({ children, ...props }) {
        return (
          <a {...props} target="_blank">
            {children}
          </a>
        );
      },
      cite: ({ document, children }) => (
        <Tooltip
          title={
            <div>
              <strong>From this document: </strong>
              <em>{document}</em>
            </div>
          }
          trigger="click"
          className="ask_mantra_ai_reference_tooltip"
        >
          <strong>
            <Button className="ask_mantra_ai_reference" type="link">
              {children}
            </Button>
          </strong>
        </Tooltip>
      ),
    }}
  >
    {content}
  </ReactMarkdown>
);

const ChainlitMarkdownMessage = ({ content, context }) => {
  const parts = content
    ? content
        .trim()
        .split("</think>")
        .map((part) => part.trim())
    : [];
  const partsLength = useMemo(() => parts.length, [parts]);
  const [previousPartsLength, setPreviousPartsLength] = useState(partsLength);
  const [activeKey, setActiveKey] = useState(0);

  useEffect(() => {
    if (previousPartsLength !== partsLength) {
      setActiveKey(partsLength - 1);
      setPreviousPartsLength(partsLength);
    }
  }, [partsLength, previousPartsLength]);

  return (
    <Collapse
      onChange={(key) => {
        setActiveKey(key);
      }}
      bordered={false}
      activeKey={activeKey}
      expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
      className="site-collapse-custom-collapse"
    >
      {context ? (
        <Panel header="Gathering context..." key="-1" className="site-collapse-custom-panel">
          <div className="ask_mantra_ai_context">
            <ChainlitMarkdownMessagePart content={context} />
          </div>
        </Panel>
      ) : null}
      {parts.map((part, index) => (
        <Panel
          header={part.startsWith("<think>") ? "Reasoning query..." : "Response"}
          key={index}
          className="site-collapse-custom-panel"
        >
          <div
            className={classNames({
              ask_mantra_ai_think: part.startsWith("<think>"),
            })}
          >
            <ChainlitMarkdownMessagePart
              content={part.startsWith("<think>") ? part.substring(7) : part}
            />
          </div>
        </Panel>
      ))}
    </Collapse>
  );
};

const ChainlitMarkdownQuestion = ({ content }) => <ChainlitMarkdownMessagePart content={content} />;

const ChainlitMessageResponse = ({ message, context }) => (
  <div className="flex mb-4">
    <div>
      <Avatar src={mantraIcon} size={32} className="!bg-[#28b87a]">
        M
      </Avatar>
    </div>
    <div className="ml-3">
      <span className="text-[#44596C] user_name">Mantra</span>
      {message?.output || context?.output ? (
        <ChainlitMarkdownMessage content={message?.output} context={context?.output} />
      ) : (
        <div>
          <Spin size="small" />
        </div>
      )}
    </div>
  </div>
);

const ChainlitMessage = ({ message }) => {
  const user = useUserStore((state) => state.user);
  return (
    <>
      <div className="flex mb-4">
        <div>
          <UserAvatar size={32} className="mr-xs" />
        </div>
        <div className="ml-3">
          <span className="text-[#44596C] user_name">{user?.name}</span>
          <ChainlitMarkdownQuestion content={message.output} />
        </div>
      </div>
      <ChainlitMessageResponse
        message={message?.steps?.[0]?.steps?.find((step) => step?.name === "MANTRA")}
        context={message?.steps?.[0]?.steps?.find((step) => step?.name === "combine_documents")}
      />
    </>
  );
};

export default ChainlitMessage;
